import { SettlementBreakdownView } from '../../../../models/payroll';
import type { ComplianceCountry } from '../../../../models/general';
import { appDate } from '../../../../utils/appDateFormat';

// Composes a bilingual (English LTR + Arabic RTL) final-settlement letter as a self-contained HTML
// string for utils/htmlToPdf.ts::downloadHtmlAsPdf. The browser shapes the Arabic + runs the bidi
// algorithm, then html2canvas screenshots the shaped DOM into the PDF (see htmlToPdf.ts). No i18next
// here on purpose: this is a legal document rendered in BOTH languages regardless of the UI locale,
// so the strings are the document content, not app chrome.

export interface SettlementLetterOptions {
  companyName: string;
  companyLogoUrl?: string;
  employeeName: string;
  staffNumber?: string;
  currencyLabel: string;
  country: ComplianceCountry;
  /** Country-aware EOS gratuity citation (models/payroll.ts gratuityLegalReferenceFor). */
  gratuityReference: string;
  /** Country-aware settlement deduction citation (models/payroll.ts settlementDeductionRefFor). */
  deductionReference: string;
  generatedOn?: Date;
}

const esc = (s: string): string =>
  s.replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string
  ));

// CO-804: `label` is an ISO currency CODE (bidi class L), never a symbol. An Arabic-script symbol
// here would open an RTL run and pull the amount's digits into it — and this letter is rendered
// through html2canvas, so what the browser shapes is exactly what lands in the PDF.
const money = (value: number, label: string): string => {
  const n = Number.isFinite(value) ? value : 0;
  const formatted = n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return label ? `${formatted} ${esc(label)}` : formatted;
};

const day = (value?: string | Date): string => {
  if (!value) return '—';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return appDate(d);
};

interface Row {
  en: string;
  ar: string;
  amount: number;
  kind?: 'deduction' | 'total';
}

export function buildSettlementLetterHtml(
  breakdown: SettlementBreakdownView,
  opts: SettlementLetterOptions,
): string {
  const label = opts.currencyLabel ?? '';
  const generated = opts.generatedOn ?? new Date();

  const rows: Row[] = [
    { en: 'End of service gratuity', ar: 'مكافأة نهاية الخدمة', amount: breakdown.gratuityAmount },
    {
      en: `Leave encashment (${breakdown.leaveDaysEncashed} days)`,
      ar: `بدل الإجازات (${breakdown.leaveDaysEncashed} يومًا)`,
      amount: breakdown.leaveEncashmentAmount,
    },
    { en: 'Prorated final month', ar: 'راتب الشهر الأخير التناسبي', amount: breakdown.proratedFinalMonthAmount },
  ];
  if (breakdown.compensationInLieuAmount > 0) {
    rows.push({ en: 'Compensation in lieu of notice', ar: 'بدل الإشعار', amount: breakdown.compensationInLieuAmount });
  }
  // Q11 — approved hourly work dated after a back-dated exit date. The letter is the employee's own
  // record of what the settlement paid, so an amount folded into the total has to be named in it.
  if ((breakdown.approvedHourlyAfterExitAmount ?? 0) > 0) {
    const shifts = breakdown.approvedHourlyAfterExitCount ?? 0;
    rows.push({
      en: `Approved hourly pay after exit date (${shifts} shifts)`,
      ar: `أجر بالساعة معتمد بعد تاريخ انتهاء الخدمة (${shifts} ورديات)`,
      amount: breakdown.approvedHourlyAfterExitAmount ?? 0,
    });
  }
  if (breakdown.totalDeductions > 0) {
    rows.push({ en: 'Deductions', ar: 'الاستقطاعات', amount: breakdown.totalDeductions, kind: 'deduction' });
  }
  if (breakdown.employerLoanDeductions != null && breakdown.employerLoanDeductions > 0) {
    rows.push({
      en: 'of which employer-loan repayment',
      ar: 'منها سداد قرض صاحب العمل',
      amount: breakdown.employerLoanDeductions,
      kind: 'deduction',
    });
  }

  const rowsHtml = rows
    .map((r) => {
      const isDeduction = r.kind === 'deduction';
      const amountText = `${isDeduction ? '−' : ''}${money(r.amount, label)}`;
      const amountColor = isDeduction ? '#b91c1c' : '#111827';
      const indent = r.en.startsWith('of which') ? 'padding-inline-start:22px;' : '';
      return `<tr>
        <td style="padding:9px 4px;border-bottom:1px solid #e5e7eb;${indent}">
          <div dir="ltr" style="font-size:12px;color:#374151;">${esc(r.en)}</div>
          <div dir="rtl" style="font-size:12px;color:#6b7280;">${esc(r.ar)}</div>
        </td>
        <td dir="ltr" style="padding:9px 4px;border-bottom:1px solid #e5e7eb;text-align:end;white-space:nowrap;font-variant-numeric:tabular-nums;font-weight:500;color:${amountColor};">${amountText}</td>
      </tr>`;
    })
    .join('');

  const logoHtml = opts.companyLogoUrl
    ? `<img src="${esc(opts.companyLogoUrl)}" alt="" style="height:44px;width:auto;object-fit:contain;" />`
    : '';

  const paidLine = opts.staffNumber
    ? `<span dir="ltr">Staff no. / الرقم الوظيفي: ${esc(opts.staffNumber)}</span>`
    : '';

  return `<div style="font-family:'Inter','Helvetica Neue',Arial,sans-serif;color:#111827;background:#ffffff;padding:40px 44px;line-height:1.5;">
    <div style="display:flex;align-items:center;justify-content:space-between;gap:16px;border-bottom:2px solid #304EA1;padding-bottom:16px;">
      <div>
        <div style="font-size:18px;font-weight:600;color:#111827;">${esc(opts.companyName || 'Company')}</div>
        <div style="font-size:11px;color:#6b7280;margin-top:2px;">${esc(opts.country)} · ${esc(day(generated))}</div>
      </div>
      ${logoHtml}
    </div>

    <div style="margin-top:24px;text-align:center;">
      <div dir="ltr" style="font-size:17px;font-weight:600;letter-spacing:0.02em;">Final Settlement Statement</div>
      <div dir="rtl" style="font-size:16px;font-weight:600;color:#374151;margin-top:2px;">بيان التسوية النهائية</div>
    </div>

    <div style="margin-top:20px;display:flex;justify-content:space-between;gap:16px;font-size:12px;color:#374151;flex-wrap:wrap;">
      <div>
        <div dir="ltr"><strong>Employee / الموظف:</strong> ${esc(opts.employeeName || '—')}</div>
        <div style="margin-top:4px;">${paidLine}</div>
      </div>
      <div style="text-align:end;">
        <div dir="ltr"><strong>Last working day / آخر يوم عمل:</strong> ${esc(day(breakdown.lastWorkingDay))}</div>
      </div>
    </div>

    <table style="width:100%;border-collapse:collapse;margin-top:18px;">
      <thead>
        <tr>
          <th style="text-align:start;padding:8px 4px;border-bottom:2px solid #d1d5db;font-size:11px;text-transform:uppercase;letter-spacing:0.04em;color:#6b7280;">Description / البيان</th>
          <th style="text-align:end;padding:8px 4px;border-bottom:2px solid #d1d5db;font-size:11px;text-transform:uppercase;letter-spacing:0.04em;color:#6b7280;">Amount / المبلغ</th>
        </tr>
      </thead>
      <tbody>
        ${rowsHtml}
        <tr>
          <td style="padding:12px 4px;">
            <div dir="ltr" style="font-size:13px;font-weight:600;">Net settlement payable</div>
            <div dir="rtl" style="font-size:13px;font-weight:600;color:#374151;">صافي المستحق النهائي</div>
          </td>
          <td dir="ltr" style="padding:12px 4px;text-align:end;white-space:nowrap;font-variant-numeric:tabular-nums;font-size:15px;font-weight:600;color:#304EA1;">${money(breakdown.finalSettlementAmount, label)}</td>
        </tr>
      </tbody>
    </table>

    <div style="margin-top:22px;padding:14px 16px;background:#f8fafc;border:1px solid #e5e7eb;border-radius:8px;font-size:11px;color:#4b5563;">
      <div dir="ltr">This statement is issued in accordance with ${esc(opts.gratuityReference)} (gratuity) and ${esc(opts.deductionReference)} (deduction limits).</div>
      <div dir="rtl" style="margin-top:6px;">صدر هذا البيان وفقًا لأحكام ${esc(opts.gratuityReference)} (المكافأة) و${esc(opts.deductionReference)} (حدود الاستقطاع).</div>
    </div>

    <div style="margin-top:40px;display:flex;justify-content:space-between;gap:40px;font-size:12px;color:#374151;">
      <div style="flex:1;border-top:1px solid #9ca3af;padding-top:8px;">
        <div dir="ltr">Employee signature</div>
        <div dir="rtl" style="color:#6b7280;">توقيع الموظف</div>
      </div>
      <div style="flex:1;border-top:1px solid #9ca3af;padding-top:8px;text-align:end;">
        <div dir="ltr">Authorized signatory</div>
        <div dir="rtl" style="color:#6b7280;">التوقيع المعتمد</div>
      </div>
    </div>
  </div>`;
}
